import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/kit/Button';
import { PhoneInput } from '../../components/form/PhoneInput';
import { useAuth } from '../../lib/auth';
import { haptic } from '../../lib/haptic';
import { AuthScreenShell } from '../../components/auth/AuthScreenShell';
import { redeemInviteCode } from '../../lib/api-client';
import { recallPhone } from '../../lib/last-phone';
import { isProfileComplete } from '../../lib/profile-completeness';

// India mobile numbers always start with 6/7/8/9 — reject early to avoid
// a Firebase round-trip + a vague auth/invalid-phone-number error message.
const PHONE_RE = /^[6-9]\d{9}$/;

// Invite codes are 4–12 uppercase alphanumeric characters.
const CODE_RE = /^[A-Z0-9]{4,12}$/;
const CODE_MAX = 12;

export function PhoneRoute() {
  const { t } = useTranslation('auth');
  // Prefill from a remembered number so a dropped-session recovery (GH #502) is
  // one tap + one SMS, not a full re-type. Empty on a fresh install.
  const [phone, setPhone] = useState(() => recallPhone());
  const [code, setCode] = useState('');
  // 'code' = primary (phone + invite code); 'otp' = secondary (phone-only + Firebase OTP)
  const [view, setView] = useState<'code' | 'otp'>('code');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const requestOtp = useAuth((s) => s.requestOtp);
  const authError = useAuth((s) => s.error);
  const clearError = useAuth((s) => s.clearError);
  const setAuth = useAuth.setState;
  const nav = useNavigate();

  const phoneValid = PHONE_RE.test(phone);
  const codeClean = code.trim().replace(/[^A-Z0-9]/g, '');
  const codeValid = CODE_RE.test(codeClean);

  const error = localError ?? authError;

  function switchView(next: 'code' | 'otp') {
    clearError();
    setLocalError(null);
    setView(next);
  }

  // Primary flow: redeem invite code (no SMS required, activates the account)
  const onSubmitCode = async () => {
    if (!phoneValid || !codeValid) {
      haptic.warn();
      return;
    }
    haptic.select();
    setLocalError(null);
    clearError();
    setSubmitting(true);
    try {
      const { driver } = await redeemInviteCode(`+91${phone}`, codeClean);
      setAuth({ status: 'authenticated', driver, pending: null, error: null });
      if (isProfileComplete(driver)) {
        nav('/', { replace: true });
      } else {
        nav('/auth/signup', { replace: true, state: { prefill: driver } });
      }
    } catch (e: any) {
      const errCode: string = e?.code ?? 'unknown';
      if (errCode === 'INVALID_CODE') {
        setLocalError(t('inviteInvalidCode'));
      } else {
        setLocalError(t('inviteGenericError', { code: errCode }));
      }
      haptic.warn();
      setSubmitting(false);
    }
  };

  // Secondary flow: Firebase phone OTP (for already-activated drivers)
  const onSubmitOtp = async () => {
    if (!phoneValid) {
      haptic.warn();
      return;
    }
    haptic.select();
    setLocalError(null);
    clearError();
    setSubmitting(true);
    try {
      await requestOtp(`+91${phone}`);
      nav('/auth/otp');
    } catch {
      // requestOtp sets authError on the store; no extra handling needed here
      setSubmitting(false);
      haptic.warn();
    }
  };

  if (view === 'otp') {
    return (
      <AuthScreenShell
        anchor={t('driverAnchor')}
        actionVerb={t('actionVerb')}
        subhead={t('subhead')}
        footer={
          <button
            type="button"
            onClick={() => switchView('code')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--green-700)',
              fontWeight: 600,
              fontSize: 'inherit',
              cursor: 'pointer',
            }}
          >
            {t('loginUseInviteCode')}
          </button>
        }
      >
        <form onSubmit={(e) => { e.preventDefault(); void onSubmitOtp(); }}>
          <label
            className="block"
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--color-text)',
              marginBottom: '8px',
            }}
          >
            {t('mobileNumber')}
          </label>
          <PhoneInput value={phone} onChange={setPhone} autoFocus />
          {error ? (
            <p
              style={{
                fontSize: '14px',
                color: 'var(--color-danger)',
                marginTop: '12px',
              }}
            >
              {error}
            </p>
          ) : null}
          <div style={{ marginTop: '22px' }}>
            <Button type="submit" disabled={!phoneValid} loading={submitting}>
              {t('sendOtp')}
            </Button>
          </div>
        </form>
      </AuthScreenShell>
    );
  }

  // Default view: phone + invite code
  return (
    <AuthScreenShell
      anchor={t('driverAnchor')}
      actionVerb={t('actionVerb')}
      subhead={t('loginPhoneAndCodeSubhead')}
      footer={
        <button
          type="button"
          onClick={() => switchView('otp')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'var(--green-700)',
            fontWeight: 600,
            fontSize: 'inherit',
            cursor: 'pointer',
          }}
        >
          {t('loginAlreadyActivated')}
        </button>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void onSubmitCode(); }}>
        <label
          className="block"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--color-text)',
            marginBottom: '8px',
          }}
        >
          {t('mobileNumber')}
        </label>
        <PhoneInput value={phone} onChange={setPhone} autoFocus />

        <label
          className="block"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--color-text)',
            marginBottom: '8px',
            marginTop: '16px',
          }}
        >
          {t('loginCodePlaceholder')}
        </label>
        <input
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={CODE_MAX}
          value={code}
          onChange={(e) => {
            const next = e.target.value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase().slice(0, CODE_MAX);
            setCode(next);
          }}
          placeholder={t('loginCodePlaceholder')}
          className="w-full border border-[var(--color-border)] rounded-[var(--radius-md)] bg-white px-3 py-3 text-base outline-none tracking-wider"
          style={{ fontFamily: 'inherit', letterSpacing: '0.08em' }}
        />

        {error ? (
          <p
            style={{
              fontSize: '14px',
              color: 'var(--color-danger)',
              marginTop: '12px',
            }}
          >
            {error}
          </p>
        ) : null}

        <div style={{ marginTop: '22px' }}>
          <Button
            type="submit"
            disabled={!phoneValid || !codeValid}
            loading={submitting}
          >
            {submitting ? t('inviteSubmitting') : t('loginPrimaryAction')}
          </Button>
        </div>
      </form>
    </AuthScreenShell>
  );
}
